import type { Filter, FilterDataOption, FiltersGroup, FiltersGroups } from '@/app/common/filters/core/filtersTypes.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import type { QuickFilter, QuickFilterTarget } from '@/app/common/filters/quickFilters/quickFilterCatalogue.ts';
import type { ChipBehaviour } from '@/app/common/filters/types/filterTypeDefinition.ts';
import { chipBehaviourOf } from '@/app/common/filters/types/filterTypeBehaviour.ts';

export type ResolvedQuickFilter = QuickFilter &
  QuickFilterTarget & {
    filter: Filter;
    label: string;
    available: boolean;
    option: FilterDataOption;
    chips: ChipBehaviour;
  };

type ResolveQuickFiltersProps = {
  filtersGroups: FiltersGroups;
  t: TranslationFunction;
  quickFilters: QuickFilter[];
};

type ResolvedTarget = QuickFilterTarget & { filter: Filter; chips: ChipBehaviour; options: FilterDataOption[] };

const resolveTarget = (filtersFlat: Filter[], target: QuickFilterTarget): ResolvedTarget | undefined => {
  const filter = filtersFlat.find((candidate) => candidate.name === target.filterName);
  const chips = filter && chipBehaviourOf(filter);
  if (!filter || !chips) return undefined;

  const allOptions = chips.options(filter);
  const options = target.values.map((value) => allOptions.find((option) => option.value === value));
  if (options.some((option) => !option)) return undefined;

  return { ...target, filter, chips, options: options as FilterDataOption[] };
};

export const resolveQuickFilters = ({
  filtersGroups,
  t,
  quickFilters,
}: ResolveQuickFiltersProps): ResolvedQuickFilter[] => {
  const filtersFlat: Filter[] = filtersGroups.flatMap((group) =>
    'filters' in group ? (group as FiltersGroup).filters : []
  );

  return quickFilters.flatMap((quickFilter) => {
    const resolved = quickFilter.targets
      .map((target) => resolveTarget(filtersFlat, target))
      .find((candidate): candidate is ResolvedTarget => !!candidate);
    if (!resolved) return [];

    const { options, ...target } = resolved;
    const available = options.filter((option) => !option.disabled);
    const option = available[0] ?? options[0];
    const translated = quickFilter.translationKey
      ? t(quickFilter.translationKey, quickFilter.translationValues)
      : undefined;

    return [
      {
        ...quickFilter,
        ...target,
        option,
        label: quickFilter.buildLabel?.(t, target.values) || translated || option.label,
        available: available.length > 0,
      },
    ];
  });
};
